import { videoCallChannelName } from "../../networking/urlEndPoints";
import { getAblyRealtime } from "../../networking/ablyClient";
import { subscribeToDeliveryVideoCallSocketUpdates } from "../../services/realtime/realtimeSocket";

const pharmacyNotificationChannelName = "pharmacy-notifications";
const CONNECTION_TIMEOUT_MS = 10000;
const SIGNAL_DEDUPE_TTL_MS = 60000;

let sharedConnectionPromise = null;

// Delegates to the single app-wide Ably client (see networking/ablyClient.js).
// Previously this module created its own separate connection, which — together with
// App.js's getSharedDriverAppRealtime — produced two competing connections on the
// same pharmacy-notifications channel and caused Android to miss realtime events.
const getSharedRealtime = () => {
  const client = getAblyRealtime();
  if (!client) {
    throw new Error("Missing Ably API key");
  }
  return client;
};

const waitForConnection = (ably) => {
  if (!ably) {
    return Promise.reject(new Error("Ably client unavailable"));
  }

  if (ably.connection.state === "connected") {
    return Promise.resolve(ably);
  }

  if (!sharedConnectionPromise) {
    sharedConnectionPromise = new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        sharedConnectionPromise = null;
        reject(new Error("Timed out waiting for Ably connection"));
      }, CONNECTION_TIMEOUT_MS);

      const onStateChange = (stateChange) => {
        if (stateChange.current === "connected") {
          clearTimeout(timeoutId);
          sharedConnectionPromise = null;
          ably.connection.off(onStateChange);
          resolve(ably);
          return;
        }

        if (["failed", "closed"].includes(stateChange.current)) {
          clearTimeout(timeoutId);
          sharedConnectionPromise = null;
          ably.connection.off(onStateChange);
          reject(new Error(`Ably connection ${stateChange.current}`));
        }
      };

      ably.connection.on(onStateChange);
      ably.connect();
    });
  }

  return sharedConnectionPromise;
};

const createSubscriptionHandle = (channel, eventName, listener) => ({
  connection: getSharedRealtime().connection,
  close: () => {
    try {
      channel.unsubscribe(eventName, listener);
    } catch (error) {}
  },
});

const createSignalDeduper = () => {
  const seen = new Map();

  return (payload) => {
    const signalId = payload?.signal_id;
    if (!signalId) return false;

    const now = Date.now();
    seen.forEach((seenAt, id) => {
      if (now - seenAt > SIGNAL_DEDUPE_TTL_MS) seen.delete(id);
    });

    if (seen.has(signalId)) return true;
    seen.set(signalId, now);
    return false;
  };
};

const combineSubscriptionHandles = (...handles) => ({
  connection: handles.find((handle) => handle?.connection)?.connection ?? null,
  close: () => {
    handles.forEach((handle) => {
      try {
        handle?.close?.();
      } catch (_) {}
    });
  },
});

const subscribeToDualTransportSignal = ({
  pharmacyId,
  subscribeToAbly,
  matchesSocketPayload,
  onEvent,
}) => {
  if (typeof onEvent !== "function") {
    return combineSubscriptionHandles();
  }

  const isDuplicate = createSignalDeduper();
  const deliver = (payload) => {
    if (isDuplicate(payload)) return;
    onEvent(payload);
  };

  let ablyHandle = null;
  try {
    ablyHandle = subscribeToAbly(deliver);
  } catch (_) {}

  const socketHandle = subscribeToDeliveryVideoCallSocketUpdates({
    pharmacyId,
    onEvent: (payload) => {
      if (matchesSocketPayload(payload)) deliver(payload);
    },
  });

  return combineSubscriptionHandles(ablyHandle, socketHandle);
};

export const primeVideoCallRealtimeConnection = async () => {
  const ably = getSharedRealtime();
  await waitForConnection(ably);
  return ably;
};

const getPharmacyEventName = (pharmacyId) => `pharmacy-${pharmacyId}`;

const buildDeliveryVideoCallSignal = ({
  pharmacyId,
  pharmacistId,
  callId,
  driverId,
  status,
  patientId,
}) => ({
  type:
    status === "requesting"
      ? "delivery_video_call_request"
      : "delivery_video_call_status",
  pharmacy_id: pharmacyId != null ? String(pharmacyId).trim() : undefined,
  pharmacist_id: pharmacistId != null ? String(pharmacistId).trim() : undefined,
  call_id: callId,
  driver_id: driverId != null ? String(driverId).trim() : undefined,
  patient_id: patientId != null ? String(patientId).trim() : undefined,
  status,
  timestamp: Date.now(),
});

/**
 * Builds the video call request payload from initiate-video-call API response.
 * Excludes patient.phn and patient.phone; adds driver id and name.
 * @param {Object} initiateApiResponse - API response from initiate-video-call-to-pharmacist
 * @param {string} pharmacyId - Pharmacy ID
 * @param {string} driverId - Driver ID
 * @param {string} driverName - Driver name
 * @param {string} callId - Generated call ID
 * @returns {Object} - Message payload to publish
 */
const buildVideoCallRequestPayload = (
  initiateApiResponse,
  pharmacyId,
  driverId,
  driverName,
  callId,
) => {
  const patient = initiateApiResponse?.patient;
  const pharmacist = initiateApiResponse?.pharmacist;
  const patientId = patient?.id;
  const patientFirstName = patient?.first_name;
  const patientLastName = patient?.last_name;
  const patientPreferredName = patient?.preferred_name;
  const patientName = [patientLastName, patientFirstName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const rawToken = initiateApiResponse?.vonage_token;
  const rawSessionId = initiateApiResponse?.vonage_session_id;
  const rawApiKey =
    initiateApiResponse?.application_id ?? initiateApiResponse?.vonage_api_key;
  return {
    callId,
    driverId,
    driverName,
    pharmacyId: pharmacyId != null ? String(pharmacyId).trim() : undefined,
    pharmacistId: pharmacist?.id,
    patientId,
    patientName,
    patientPreferredName,
    patientLastName,
    patientFirstName,
    timestamp: Date.now(),
    status: "requesting",
    vonage_session_id:
      rawSessionId != null
        ? String(rawSessionId).trim().replace(/\s+/g, "")
        : undefined,
    vonage_token:
      rawToken != null
        ? String(rawToken).trim().replace(/\s+/g, "")
        : undefined,
    application_id:
      rawApiKey != null
        ? String(rawApiKey).trim().replace(/\s+/g, "")
        : undefined,
    // Carry the provider the backend pinned for this call through to the
    // pharmacist side. Without these two fields a MediTalk call would arrive
    // looking like a Vonage call with no API key and fail to connect.
    video_provider:
      initiateApiResponse?.video_provider != null
        ? String(initiateApiResponse.video_provider).trim()
        : undefined,
    livekit_url:
      initiateApiResponse?.livekit_url != null
        ? String(initiateApiResponse.livekit_url).trim()
        : undefined,
    patient: patient
      ? {
          id: patient.id,
          first_name: patient.first_name,
          last_name: patient.last_name,
          preferred_name: patient.preferred_name,
        }
      : undefined,
    pharmacist: pharmacist
      ? { id: pharmacist.id, name: pharmacist.name }
      : undefined,
    driver: { id: driverId, name: driverName },
  };
};

export const publishVideoCallRequest = async ({
  pharmacyId,
  pharmacistId: pharmacistIdParam,
  driverId,
  driverName,
  patientId,
  patientName,
  patientLastName,
  patientFirstName,
  initiateApiResponse,
  callId: callIdParam,
}) => {
  const ably = getSharedRealtime();
  await waitForConnection(ably);
  const channel = ably.channels.get(videoCallChannelName);

  const callId =
    callIdParam ??
    initiateApiResponse?.vonage_session_id ??
    `call-${Date.now()}-${driverId}`;
  const pharmacistId = initiateApiResponse?.pharmacist?.id ?? pharmacistIdParam;
  if (!pharmacistId) {
    throw new Error(
      "pharmacistId required (from initiateApiResponse.pharmacist.id or param)",
    );
  }

  const requestChannel = `video-call-request-${pharmacyId}-${pharmacistId}`;
  const pharmacyChannel = ably.channels.get(pharmacyNotificationChannelName);
  const pharmacyEvent = getPharmacyEventName(pharmacyId);

  const message = initiateApiResponse
    ? buildVideoCallRequestPayload(
        initiateApiResponse,
        pharmacyId,
        driverId,
        driverName,
        callId,
      )
    : {
        callId,
        driverId,
        driverName,
        pharmacyId: pharmacyId != null ? String(pharmacyId).trim() : undefined,
        pharmacistId,
        patientId,
        patientName:
          patientName ??
          `${patientLastName ?? ""} ${patientFirstName ?? ""}`.trim(),
        patientLastName,
        patientFirstName,
        timestamp: Date.now(),
        status: "requesting",
      };

  await Promise.all([
    channel.publish(requestChannel, message),
    pharmacyChannel.publish(
      pharmacyEvent,
      buildDeliveryVideoCallSignal({
        pharmacyId,
        pharmacistId,
        callId,
        driverId,
        patientId: message.patientId,
        status: "requesting",
      }),
    ),
  ]);

  return { callId, ably };
};

export const subscribeToVideoCallResponse = ({
  pharmacyId,
  driverId,
  onResponse,
}) => {
  const responseChannel = `video-call-response-${pharmacyId}-${driverId}`;

  return subscribeToDualTransportSignal({
    pharmacyId,
    matchesSocketPayload: (payload) =>
      payload?.signal_type === "driver_response" &&
      Number(payload?.driver_id) === Number(driverId),
    onEvent: onResponse,
    subscribeToAbly: (deliver) => {
      const ably = getSharedRealtime();
      const channel = ably.channels.get(videoCallChannelName);
      const listener = (message) => {
        if (message.name === responseChannel) deliver(message.data);
      };
      channel.subscribe(responseChannel, listener);
      return createSubscriptionHandle(channel, responseChannel, listener);
    },
  });
};

export const subscribeToDeliveryVideoCallNotifications = ({
  pharmacyId,
  onEvent,
}) => {
  const eventName = `pharmacy-${pharmacyId}`;

  return subscribeToDualTransportSignal({
    pharmacyId,
    matchesSocketPayload: (payload) =>
      Number(payload?.pharmacy_id ?? payload?.pharmacyId) === Number(pharmacyId),
    onEvent,
    subscribeToAbly: (deliver) => {
      const ably = getSharedRealtime();
      const channel = ably.channels.get(pharmacyNotificationChannelName);
      const listener = (message) => {
        if (message.name === eventName) deliver(message.data);
      };
      channel.subscribe(eventName, listener);
      return createSubscriptionHandle(channel, eventName, listener);
    },
  });
};

export const subscribeToDriverClinicCallUpdates = ({
  pharmacyId,
  onEvent,
}) => {
  const ably = getSharedRealtime();
  const channel = ably.channels.get(pharmacyNotificationChannelName);
  const eventName = `pharmacy-${pharmacyId}`;

  const listener = (message) => {
    const data = message?.data;
    if (
      message.name === eventName &&
      data?.type === "driver-clinic-call-updated" &&
      typeof onEvent === "function"
    ) {
      onEvent(data);
    }
  };

  channel.subscribe(eventName, listener);

  return createSubscriptionHandle(channel, eventName, listener);
};

export const subscribeToVideoCallRequest = ({
  pharmacyId,
  pharmacistId,
  onRequest,
}) => {
  const ably = getSharedRealtime();
  const channel = ably.channels.get(videoCallChannelName);
  const requestChannel = `video-call-request-${pharmacyId}-${pharmacistId}`;

  const listener = (message) => {
    if (message.name === requestChannel && typeof onRequest === "function") {
      onRequest(message.data);
    }
  };

  channel.subscribe(requestChannel, listener);

  return createSubscriptionHandle(channel, requestChannel, listener);
};

export const publishVideoCallResponse = async ({
  pharmacyId,
  driverId,
  callId,
  status,
  pharmacistId,
  pharmacistName,
  reason,
}) => {
  const ably = getSharedRealtime();
  await waitForConnection(ably);
  const channel = ably.channels.get(videoCallChannelName);
  const responseChannel = `video-call-response-${pharmacyId}-${driverId}`;

  const message = {
    callId,
    status,
    pharmacistId,
    pharmacistName,
    timestamp: Date.now(),
  };

  if (reason) {
    message.reason = reason;
  }

  await channel.publish(responseChannel, message);
};

export const publishVideoCallCancelled = async ({
  pharmacyId,
  pharmacistId,
  callId,
  driverId,
  driverName,
  patientId,
  patientName,
}) => {
  const ably = getSharedRealtime();
  await waitForConnection(ably);
  const channel = ably.channels.get(videoCallChannelName);
  const pharmacyChannel = ably.channels.get(pharmacyNotificationChannelName);
  const requestChannel = `video-call-request-${pharmacyId}-${pharmacistId}`;
  const pharmacyEvent = getPharmacyEventName(pharmacyId);

  await Promise.all([
    channel.publish(requestChannel, {
      callId,
      driverId,
      driverName,
      pharmacistId,
      patientId,
      patientName,
      timestamp: Date.now(),
      status: "cancelled",
    }),
    pharmacyChannel.publish(
      pharmacyEvent,
      buildDeliveryVideoCallSignal({
        pharmacyId,
        pharmacistId,
        callId,
        driverId,
        patientId,
        status: "cancelled",
      }),
    ),
  ]);
};

export const publishVideoCallRequestStatus = async ({
  pharmacyId,
  pharmacistId,
  callId,
  status,
}) => {
  const ably = getSharedRealtime();
  await waitForConnection(ably);
  const channel = ably.channels.get(videoCallChannelName);
  const pharmacyChannel = ably.channels.get(pharmacyNotificationChannelName);
  const requestChannel = `video-call-request-${pharmacyId}-${pharmacistId}`;
  const pharmacyEvent = getPharmacyEventName(pharmacyId);

  await Promise.all([
    channel.publish(requestChannel, {
      callId,
      status,
      timestamp: Date.now(),
    }),
    pharmacyChannel.publish(
      pharmacyEvent,
      buildDeliveryVideoCallSignal({
        pharmacyId,
        pharmacistId,
        callId,
        status,
      }),
    ),
  ]);
};

const getVideoCallSessionEventName = (callId) => `video-call-session-${callId}`;
const getPharmacistDriverVideoCallSessionEventName = (callId) =>
  `pharmacist-driver-video-call-session-${callId}`;

export const publishVideoCallEnded = async (callId, endedBy) => {
  if (!callId) return;

  const ably = getSharedRealtime();
  await waitForConnection(ably);
  const channel = ably.channels.get(videoCallChannelName);
  const eventName = getVideoCallSessionEventName(callId);
  await channel.publish(eventName, {
    status: "ended",
    endedBy,
    callId,
    timestamp: Date.now(),
  });
};

export const subscribeToVideoCallSession = (callId, onEnded, { pharmacyId } = {}) => {
  if (!callId || typeof onEnded !== "function") return null;

  if (!pharmacyId) {
    try {
      const ably = getSharedRealtime();
      const channel = ably.channels.get(videoCallChannelName);
      const eventName = getVideoCallSessionEventName(callId);
      const listener = (message) => {
        if (message.name === eventName && message.data?.status === "ended") onEnded(message.data);
      };
      channel.subscribe(eventName, listener);
      return createSubscriptionHandle(channel, eventName, listener);
    } catch (_) {
      return null;
    }
  }

  const eventName = getVideoCallSessionEventName(callId);
  return subscribeToDualTransportSignal({
    pharmacyId,
    matchesSocketPayload: (payload) =>
      ["session_ended", "pharmacist_driver_session_ended"].includes(payload?.signal_type) &&
      String(payload?.call_id ?? payload?.callId) === String(callId) &&
      payload?.status === "ended",
    onEvent: onEnded,
    subscribeToAbly: (deliver) => {
      const ably = getSharedRealtime();
      const channel = ably.channels.get(videoCallChannelName);
      const listener = (message) => {
        if (message.name === eventName && message.data?.status === "ended") deliver(message.data);
      };
      channel.subscribe(eventName, listener);
      return createSubscriptionHandle(channel, eventName, listener);
    },
  });
};

export const subscribeToPharmacistDriverVideoCallSession = (
  callId,
  onEnded,
  { pharmacyId } = {},
) => {
  if (!callId || typeof onEnded !== "function") return null;

  if (pharmacyId) {
    const eventName = getPharmacistDriverVideoCallSessionEventName(callId);
    return subscribeToDualTransportSignal({
      pharmacyId,
      matchesSocketPayload: (payload) =>
        payload?.signal_type === "pharmacist_driver_session_ended" &&
        String(payload?.call_id ?? payload?.callId) === String(callId) &&
        payload?.status === "ended",
      onEvent: onEnded,
      subscribeToAbly: (deliver) => {
        const ably = getSharedRealtime();
        const channel = ably.channels.get(videoCallChannelName);
        const listener = (message) => {
          if (message.name === eventName && message.data?.status === "ended") deliver(message.data);
        };
        channel.subscribe(eventName, listener);
        return createSubscriptionHandle(channel, eventName, listener);
      },
    });
  }

  try {
    const ably = getSharedRealtime();
    const channel = ably.channels.get(videoCallChannelName);
    const eventName = getPharmacistDriverVideoCallSessionEventName(callId);
    const listener = (message) => {
      if (message.name === eventName && message.data?.status === "ended") {
        onEnded(message.data);
      }
    };

    channel.subscribe(eventName, listener);

    return createSubscriptionHandle(channel, eventName, listener);
  } catch (error) {
    return null;
  }
};
